import { IconProp } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { FontSize } from "../../../components/Text";
import { Breakpoints } from "../../../theme/Breakpoints";
import { ThemeType } from "../../../theme/Theme";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";

export const DashboardList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const DashboardRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding-block: ${Spacing.s};
  border-top: 1px solid ${({ theme }) => theme.sub};

  &:first-child {
    border-top: none;
  }
`;

export const DashboardRowTag = styled.span<{ $tone: keyof ThemeType }>`
  flex: none;
  width: 6.5rem;
  padding: ${Spacing.xs};
  border-radius: ${Rounded.sm};
  text-align: center;
  font-size: ${FontSize.xs};
  font-weight: bold;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme, $tone }) => theme[$tone]};
  background-color: ${({ theme, $tone }) => `${theme[$tone]}1a`};

  @media (max-width: ${Breakpoints.PHONE}) {
    width: auto;
    padding-inline: ${Spacing.s};
  }
`;

export const DashboardRowWhat = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

export const DashboardRowTitle = styled(Link)`
  font-weight: 500;
  color: ${({ theme }) => theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

export const DashboardRowMeta = styled.span`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.darkerSub};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  flex: none;
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  cursor: pointer;
  font-size: ${FontSize.s};
  font-weight: bold;
  color: ${({ theme }) => theme.main};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.sub};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.main};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

const ActionLabel = styled.span`
  @media (max-width: ${Breakpoints.PHONE}) {
    display: none;
  }
`;

type DashboardRowActionProps = {
  icon: IconProp;
  label: string;
  onClick: VoidFunction;
  disabled?: boolean;
  dataTestId?: string;
};

export const DashboardRowAction: React.FC<DashboardRowActionProps> = ({
  icon,
  label,
  onClick,
  disabled,
  dataTestId,
}) => (
  <ActionButton
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={dataTestId}
  >
    <AtiraIcon icon={icon} size="sm" />
    <ActionLabel>{label}</ActionLabel>
  </ActionButton>
);

export const DashboardEmpty = styled.div`
  display: grid;
  place-items: center;
  flex: 1;
  padding-block: ${Spacing.l};
  text-align: center;
  color: ${({ theme }) => theme.darkerSub};
`;
